import { useGameRounds } from '@/api/game-round';
import { GameIDType } from '@/enums/gameId';
import { ModalsKey } from '@/enums/modalsKey';
import { GameReportType } from '@/hooks/gameReport/useGameReportSearchParams/gameReportType.enum';
import { useModal } from '@/hooks/modals/useModal';
import { ErrorChip } from '@starsoft/common/components';
import { m } from 'framer-motion';
import { useMemo } from 'react';
import LastMatches from '../../LastMatches';
import LastRoundsChip from './Chip';
import { LastRoundsChipsProps } from './props';
import { chipVariants } from './variants';

export default function LastRoundsChips({
  isBackgroundPaper,
  gameId,
  renderChip,
}: LastRoundsChipsProps) {
  const { isOpen } = useModal(ModalsKey.LastRounds);
  const { isPending, error, rounds, refetch } = useGameRounds({
    gameId: gameId as GameIDType,
  });

  console.log(rounds);

  const isLoading: boolean = !isOpen && isPending;

  const Rounds: JSX.Element[] = useMemo(
    () =>
      rounds?.map(round =>
        !renderChip ? (
          <m.div
            exit="exit"
            animate="open"
            initial="closed"
            variants={chipVariants}
            key={`last-round-chip-${round.id?.toString()}`}
            layoutId={`last-round-chip-${round.id?.toString()}`}
          >
            <LastRoundsChip
              round={round}
              modal={ModalsKey.GameReport}
              gameId={gameId}
              isBackgroundPaper={isBackgroundPaper}
            />
          </m.div>
        ) : (
          <m.div
            exit="exit"
            animate="open"
            initial="closed"
            variants={chipVariants}
            key={`last-round-chip-${round.id?.toString()}`}
            layoutId={`last-round-chip-${round.id?.toString()}`}
          >
            {renderChip({
              round,
              link: `?round-slug=${round?.slug}&round-id=${round?.id}&game-id=${gameId}&game-type=${GameReportType.RoundGame}`,
              modal: ModalsKey.GameReport,
            })}
          </m.div>
        ),
      ),
    [isBackgroundPaper, renderChip, gameId, rounds],
  );

  const Skeletons: JSX.Element[] = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) =>
        !renderChip ? (
          <LastRoundsChip
            key={`last-round-chip-${i}`}
            isLoading
            modal={ModalsKey.GameReport}
            gameId={gameId}
            isBackgroundPaper={isBackgroundPaper}
          />
        ) : (
          <div key={`last-round-chip-skeleton-${i}`}>
            {renderChip({
              isLoading: true,
              link: '',
            })}
          </div>
        ),
      ),
    [isBackgroundPaper, renderChip, gameId],
  );

  if (error) {
    return <ErrorChip action={refetch} cardBg={!isBackgroundPaper} />;
  }

  return (
    <LastMatches
      gameId={gameId}
      modal={ModalsKey.LastRounds}
      link={`?game-id=${gameId}`}
      isBackgroundPaper={isBackgroundPaper}
    >
      {isLoading ? Skeletons : Rounds}
    </LastMatches>
  );
}
